import { cloneDeep } from 'lodash';
import { useEffect, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
// Material-UI & Components
import { Box, Stack } from '@mui/material';
import { Button } from 'src/@/components/ui/button';
import Iconify from 'src/components/Iconify';
import ErrorBoundary from 'src/ErrorBoundary';
import { BackdropLoader } from 'src/components/ui-component/loading/BackdropLoader';
import DocStoreInputHandler from './DocStoreInputHandler';
import UploadSingleFile from 'src/components/upload/UploadSingleFile';
// Project import
import useApi from 'src/hooks/useApi';
import nodesApi from 'src/api/nodes';
import documentsApi from 'src/api/documentstore';
import v2_actionCriarTreinamento from 'src/actions/v2/ai/v2_actionCriarTreinamento';
import { initNode } from 'src/utils/genericHelper';

// Config constants
const storeId = '6388852d-50d5-4c0f-930a-96d9d69f958d';

const LoaderConfigPreviewChunks = ({ docLoaderNodeName, agentId, type, changeDocLoaderNodeName, onCallback }) => {
    const { enqueueSnackbar } = useSnackbar();
    const { t } = useTranslation('settings');
    const getNodeDetailsApi = useApi(nodesApi.getSpecificNode);
    const getSpecificDocumentStoreApi = useApi(documentsApi.getSpecificDocumentStore);
    const [selectedDocumentLoader, setSelectedDocumentLoader] = useState({});
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [fileToUpload, setFileToUpload] = useState(null);

    // Check if current mode is website or document
    const isWebsite = docLoaderNodeName === 'cheerioWebScraper';

    // Handle File Drop
    const handleDrop = (acceptedFiles) => {
        const file = Array.isArray(acceptedFiles) ? acceptedFiles[0] : acceptedFiles;
        if (file && file.name) {
            setFileToUpload(file);
            // Determine type based on extension
            const extension = file.name.split('.').pop().toLowerCase();
            let newNodeName = 'textFile'; // default
            if (extension === 'pdf') newNodeName = 'pdfFile';
            if (extension === 'docx') newNodeName = 'docxFile';
            if (extension === 'txt') newNodeName = 'textFile';

            // Change loader type if needed
            if (docLoaderNodeName !== newNodeName && changeDocLoaderNodeName) {
                changeDocLoaderNodeName(newNodeName);
                // The useEffect will trigger getNodeDetailsApi.request(newNodeName)
            } else {
                // If same type, just re-request to reset state logic if needed
                getNodeDetailsApi.request(docLoaderNodeName);
            }
        }
    };

    // Prepare Config for API
    const prepareConfig = () => {
        const config = {};
        config.loaderId = docLoaderNodeName;
        config.storeId = storeId;
        config.loaderName = selectedDocumentLoader?.label;
        if (selectedDocumentLoader.inputs) {
            config.loaderConfig = {};
            Object.keys(selectedDocumentLoader.inputs).forEach((key) => {
                config.loaderConfig[key] = selectedDocumentLoader.inputs[key];
            });
        }
        return config;
    };

    // Submit Action
    const handleUpload = async () => {
        if (!selectedDocumentLoader.inputs) return;
        setLoading(true);
        const config = prepareConfig();
        // Override preview count for simple upload
        config.previewChunkCount = 5;

        try {
            await v2_actionCriarTreinamento(agentId, {
                scope: {
                    type: type,
                    content: {
                        type: docLoaderNodeName,
                        chunck: config,
                    },
                },
            });
            enqueueSnackbar(t('aiAgents.training.chunksDocumentAddedSuccess', 'Documento adicionado à fila de processamento.'), { variant: 'success' });
            setFileToUpload(null);
            if (onCallback) {
                setTimeout(() => onCallback(), 1000);
            }
            // Allow simplified reset
            setFileToUpload(null);
        } catch (error) {
            console.error(error);
            enqueueSnackbar(t('aiAgents.training.chunksDocumentAddError', 'Erro ao adicionar documento.'), { variant: 'error' });
        } finally {
            setLoading(false);
        }
    };

    // Initial Load & Updates
    useEffect(() => {
        if (docLoaderNodeName) {
            getNodeDetailsApi.request(docLoaderNodeName);
        }
    }, [docLoaderNodeName]);

    // Process Node Data & Inject File
    useEffect(() => {
        if (getNodeDetailsApi.data) {
            const nodeData = cloneDeep(initNode(getNodeDetailsApi.data, uuidv4()));
            // If we have a pending file upload, inject it into the inputs
            if (fileToUpload && !isWebsite) {
                const fileParam = nodeData.inputParams.find((p) => p.type === 'file');
                if (fileParam) {
                    nodeData.inputs[fileParam.name] = fileToUpload;
                }
            }
            setSelectedDocumentLoader(nodeData);
        }
    }, [getNodeDetailsApi.data, fileToUpload]);

    // UI Rendering
    return (
        <div className="w-full">
            {error ? (
                <ErrorBoundary error={error} />
            ) : (
                <Stack direction="column" spacing={3}>
                    {/* Website Input Mode */}
                    {isWebsite && (
                        <div className="space-y-4">
                            {selectedDocumentLoader &&
                                Object.keys(selectedDocumentLoader).length > 0 &&
                                (selectedDocumentLoader.inputParams ?? [])
                                    .filter((inputParam) => !inputParam.hidden)
                                    .map((inputParam, index) => (
                                        <DocStoreInputHandler key={index} inputParam={inputParam} data={selectedDocumentLoader} />
                                    ))}
                            <Button className="w-full bg-violet-600 hover:bg-violet-700 text-white" onClick={handleUpload} disabled={loading}>
                                <Iconify icon="solar:link-circle-bold" className="mr-2" />
                                {t('aiAgents.training.chunksAddWebsiteButton', 'Adicionar Website')}
                            </Button>
                        </div>
                    )}
                    {/* File Upload Mode */}
                    {!isWebsite && (
                        <div className="space-y-4">
                            <UploadSingleFile
                                file={fileToUpload}
                                onDrop={handleDrop}
                                accept={{
                                    'application/pdf': ['.pdf'],
                                    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
                                    'text/plain': ['.txt'],
                                }}
                                sx={{
                                    height: 200,
                                    background: (theme) => (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : '#fafafa'),
                                    borderStyle: 'dashed',
                                    borderColor: 'divider',
                                }}
                            />
                            {fileToUpload && (
                                <div className="animate-in fade-in slide-in-from-top-2">
                                    <div className="flex items-center gap-3 p-3 bg-violet-50 rounded-lg border border-violet-100 mb-4">
                                        <Iconify icon="solar:file-check-bold-duotone" width={24} className="text-violet-600" />
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm font-medium text-gray-900 truncate">{fileToUpload.name}</p>
                                            <p className="text-xs text-gray-500">{(fileToUpload.size / 1024).toFixed(2)} KB</p>
                                        </div>
                                    </div>
                                    <Button
                                        className="w-full h-10 bg-violet-600 hover:bg-violet-700 text-white shadow-lg shadow-violet-600/20"
                                        onClick={handleUpload}
                                        disabled={loading}
                                    >
                                        {loading ? (
                                            <>
                                                <Iconify icon="svg-spinners:ring-resize" className="mr-2" />
                                                {t('aiAgents.training.chunksProcessingLabel', 'Processando...')}
                                            </>
                                        ) : (
                                            <>
                                                <Iconify icon="solar:upload-track-bold" className="mr-2" />
                                                {t('aiAgents.training.chunksStartTrainingButton', 'Iniciar Treinamento')}
                                            </>
                                        )}
                                    </Button>
                                    <p className="text-xs text-center text-gray-400 mt-2">{t('aiAgents.training.chunksFileProcessingHint', 'O arquivo será processado e adicionado à base de conhecimento.')}</p>
                                </div>
                            )}
                        </div>
                    )}
                </Stack>
            )}
            {loading && <BackdropLoader open={loading} />}
        </div>
    );
};

export default LoaderConfigPreviewChunks;